import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import useHover from 'ahooks/lib/useHover';
import { Input } from '@tigergraph/app-ui-lib/input';
import { useStyletron } from '@tigergraph/app-ui-lib/Theme';
import { PencilIcon } from 'lucide-react';
import { Button } from '@tigergraph/app-ui-lib/button';

export type EditableTextHandle = {
  reset: () => void;
};

type EditableTextProps = {
  text: string;
  onTextChange: (text: string) => void;
  compact?: boolean;
  maxWidth?: string;
  disableEdit?: boolean;
  onClick?: () => void;
};

const EditableText = forwardRef<EditableTextHandle, EditableTextProps>(
  ({ text, onTextChange, compact, maxWidth, disableEdit, onClick }, ref) => {
    const nameRef = useRef<HTMLSpanElement | null>(null);

    const isHovering = useHover(nameRef);
    const [isEditing, setIsEditing] = useState(false);
    const [css, theme] = useStyletron();

    const [value, setValue] = useState(text);

    let Component = 'span';
    if (onClick) {
      Component = 'button';
    }

    useEffect(() => {
      setValue(text);
    }, [text]);

    const onSubmit = () => {
      setIsEditing(false);

      // do not allow empty string
      if (value.trim() !== '' && value !== text) {
        onTextChange(value);
      } else {
        setValue(text);
      }
    };

    useImperativeHandle(
      ref,
      () => {
        return {
          reset() {
            setValue(text);
          },
        };
      },
      [text]
    );

    return (
      <span
        className={css({
          ...(maxWidth
            ? {
                maxWidth,
              }
            : {
                flexBasis: 0,
                flexGrow: 1,
                flexShrink: 1,
                minWidth: 0,
              }),
          display: 'inline-flex',
          alignItems: 'center',
          marginRight: 'auto',
        })}
        ref={nameRef}
      >
        {isEditing ? (
          <Input
            autoFocus={true}
            value={value}
            size="compact"
            onBlur={() => {
              onSubmit();
            }}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                onSubmit();
              }
            }}
            onChange={(e) => setValue(e.currentTarget.value)}
            overrides={{
              Root: {
                style: {
                  width: '200px',
                },
              },
              Input: {
                style: {
                  paddingTop: compact ? '1px' : '6px',
                  paddingBottom: compact ? '1px' : '6px',
                  paddingLeft: compact ? '4px' : '12px',
                  paddingRight: compact ? '4px' : '12px',
                  fontSize: compact ? '14px' : '16px',
                  fontWeight: compact ? 400 : 500,
                  fontFamily: compact ? 'Roboto' : 'Urbanist',
                },
              },
            }}
          />
        ) : (
          // @ts-ignore
          <Component
            className={css({
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              color: onClick ? theme.colors['text.link'] : 'inherit',
            })}
            onClick={onClick}
          >
            {value}
          </Component>
        )}
        {!disableEdit ? (
          isHovering && !isEditing ? (
            <Button
              size="compact"
              kind="text"
              shape="square"
              onClick={() => {
                setIsEditing(true);
              }}
            >
              <PencilIcon size={12} />
            </Button>
          ) : (
            <span
              className={css({
                width: '16px',
              })}
            />
          )
        ) : null}
      </span>
    );
  }
);

export default EditableText;
